import { createContext, useContext, useState, ReactNode } from 'react'

export type Environment = 'dev' | 'prod' | 'integration'

interface EnvironmentContextType {
  environment: Environment
  setEnvironment: (env: Environment) => void
  apiPrefix: string
  firestoreDatabase: string
}

const EnvironmentContext = createContext<EnvironmentContextType | null>(null)

const STORAGE_KEY = 'codewords_admin_environment'

export function EnvironmentProvider({ children }: { children: ReactNode }) {
  const [environment, setEnvironmentState] = useState<Environment>(() => {
    // Load from localStorage or default to 'dev'
    const saved = localStorage.getItem(STORAGE_KEY)
    return (saved as Environment) || 'dev'
  })

  const setEnvironment = (env: Environment) => {
    setEnvironmentState(env)
    localStorage.setItem(STORAGE_KEY, env)
  }

  // Get API prefix based on environment
  const apiPrefix = getApiPrefix(environment)

  // Get Firestore database name
  const firestoreDatabase = getFirestoreDatabase(environment)

  return (
    <EnvironmentContext.Provider value={{ environment, setEnvironment, apiPrefix, firestoreDatabase }}>
      {children}
    </EnvironmentContext.Provider>
  )
}

export function useEnvironment() {
  const context = useContext(EnvironmentContext)
  if (!context) {
    throw new Error('useEnvironment must be used within EnvironmentProvider')
  }
  return context
}

function getApiPrefix(env: Environment): string {
  switch (env) {
    case 'dev':
      return 'dev-'
    case 'prod':
      return ''
    case 'integration':
      return 'dev-' // Integration tests use dev functions
    default:
      return 'dev-'
  }
}

function getFirestoreDatabase(env: Environment): string {
  switch (env) {
    case 'dev':
      return 'codewords-dev'
    case 'prod':
      return 'codewords'
    case 'integration':
      return 'integration-tests'
    default:
      return 'codewords-dev'
  }
}
